/**
 * Preview skill: start and control a local preview server process that implements
 * the BuildAutomaton Preview Server API.
 *
 * The CLI bridge runs a configurable command (e.g. npm run preview) with PORT and
 * PREVIEW_SECRET; the process must implement GET /__preview/status and POST /__preview/stop.
 * Start/stop/status are usable over the WebSocket from the dashboard.
 */

import { spawn, type ChildProcess } from 'node:child_process';
import { getBridgeRoot } from '../files/cwd/index.js';
import type { Skill, SkillOperation } from './types';
const PREVIEW_API_BASE_PATH = '/__preview' as const;
const PREVIEW_SECRET_HEADER = 'X-Preview-Secret' as const;

interface PreviewStatusResponse {
  status: 'running' | 'stopping';
  url?: string;
  message?: string;
}

interface PreviewStopResponse {
  ok: true;
  message?: string;
}

const DEFAULT_PORT = 3000;
const DEFAULT_COMMAND = 'npm run preview';

/** Env key for the command to run to start the preview server (e.g. "npm run preview"). */
export const PREVIEW_COMMAND_ENV = 'BUILDAUTOMATON_PREVIEW_COMMAND';
/** Env key for default port (used when start is called without port param). */
export const PREVIEW_PORT_ENV = 'BUILDAUTOMATON_PREVIEW_PORT';

let previewProcess: ChildProcess | null = null;
let previewPort: number = DEFAULT_PORT;
let previewSecret: string = '';

function getPreviewCommand(): string {
  return process.env[PREVIEW_COMMAND_ENV]?.trim() || DEFAULT_COMMAND;
}

function randomSecret(): string {
  const bytes = new Uint8Array(32);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

async function requestPreviewApi<T>(
  port: number,
  secret: string,
  method: 'GET' | 'POST',
  path: string,
  body?: unknown
): Promise<T> {
  const url = `http://127.0.0.1:${port}${path}`;
  const headers: Record<string, string> = {
    [PREVIEW_SECRET_HEADER]: secret,
    'Content-Type': 'application/json',
  };
  const res = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) {
    throw new Error(data?.error ?? `Preview API ${method} ${path}: ${res.status}`);
  }
  return data as T;
}

const OPERATIONS: SkillOperation[] = [
  {
    id: 'start',
    description: 'Start the preview server process (runs the configured command with PORT and PREVIEW_SECRET).',
    params: [
      { name: 'port', description: 'Port for the preview server', type: 'number', required: false },
    ],
  },
  {
    id: 'stop',
    description: 'Stop the preview server via POST /__preview/stop (graceful shutdown).',
    params: [],
  },
  {
    id: 'status',
    description: 'Return status from GET /__preview/status (running, url).',
    params: [],
  },
];

export const previewSkill: Skill = {
  id: 'preview',
  name: 'Preview',
  description:
    'Start and manage a local preview server that implements the BuildAutomaton Preview Server API. Configure the command with BUILDAUTOMATON_PREVIEW_COMMAND (default: npm run preview). The server receives PORT and PREVIEW_SECRET and must expose /__preview/status and /__preview/stop.',
  operations: OPERATIONS,

  async execute(operationId: string, params: Record<string, unknown>): Promise<unknown> {
    const command = getPreviewCommand();
    switch (operationId) {
      case 'start': {
        if (previewProcess) {
          return {
            ok: false,
            message: 'Preview server already running',
            url: `http://localhost:${previewPort}`,
          };
        }
        const port = (params.port as number) ?? (Number(process.env[PREVIEW_PORT_ENV]) || DEFAULT_PORT);
        previewPort = port;
        previewSecret = randomSecret();

        const isWindows = process.platform === 'win32';
        const parts = command.split(/\s+/);
        const exe = parts[0];
        const args = parts.slice(1);

        previewProcess = spawn(isWindows && exe === 'npm' ? 'npm.cmd' : exe, args, {
          cwd: getBridgeRoot(),
          stdio: ['ignore', 'pipe', 'pipe'],
          env: {
            ...process.env,
            PORT: String(port),
            PREVIEW_SECRET: previewSecret,
          },
        });
        previewProcess.stdout?.on('data', (d) => process.stdout.write(d));
        previewProcess.stderr?.on('data', (d) => process.stderr.write(d));
        previewProcess.on('exit', (code) => {
          previewProcess = null;
          if (code !== null && code !== 0) {
            process.stderr.write(`Preview server exited with code ${code}\n`);
          }
        });
        return {
          ok: true,
          message: `Preview server starting (${command}, port=${port})`,
          url: `http://localhost:${port}`,
          port,
        };
      }
      case 'stop': {
        if (!previewProcess) {
          return { ok: true, message: 'No preview server was running' };
        }
        if (!previewSecret) {
          previewProcess.kill('SIGTERM');
          previewProcess = null;
          return { ok: true, message: 'Preview process stopped (no secret)' };
        }
        try {
          await requestPreviewApi<PreviewStopResponse>(
            previewPort,
            previewSecret,
            'POST',
            `${PREVIEW_API_BASE_PATH}/stop`
          );
        } catch (e) {
          previewProcess.kill('SIGTERM');
        }
        previewProcess = null;
        previewSecret = '';
        return { ok: true, message: 'Preview server stop requested' };
      }
      case 'status': {
        if (!previewProcess || !previewSecret) {
          return {
            running: previewProcess != null,
            url: previewProcess ? `http://localhost:${previewPort}` : undefined,
            message: previewProcess ? `Process running at http://localhost:${previewPort}` : 'No preview server running',
          };
        }
        try {
          const status = await requestPreviewApi<PreviewStatusResponse>(
            previewPort,
            previewSecret,
            'GET',
            `${PREVIEW_API_BASE_PATH}/status`
          );
          return {
            running: status.status === 'running',
            status: status.status,
            url: status.url ?? `http://localhost:${previewPort}`,
            message: status.message ?? (status.status === 'running' ? 'Preview server running' : 'Stopping'),
          };
        } catch {
          return {
            running: previewProcess != null,
            url: `http://localhost:${previewPort}`,
            message: 'Process running but status endpoint unreachable (server may not implement Preview Server API yet)',
          };
        }
      }
      default:
        throw new Error(`Unknown operation: ${operationId}`);
    }
  },
};
